import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";
import {
  ShoppingBagIcon, HeartIcon, UserIcon, Bars3Icon, XMarkIcon,
  MagnifyingGlassIcon, QuestionMarkCircleIcon
} from "@heroicons/react/24/outline";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";

export default function Navbar() {
  const { isAuthenticated, user, logout } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
    }
  };

  const itemCount = cart?.item_count || 0;

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-36">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src={logo} alt="Kushi Fashions" style={{ height: "140px" }} className="w-auto" />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/products" className="text-gray-600 hover:text-primary-600 transition-colors">
              Shop
            </Link>
            <Link to="/products?category=kurta" className="text-gray-600 hover:text-primary-600 transition-colors">
              Kurtas
            </Link>
            <Link to="/products?category=saree" className="text-gray-600 hover:text-primary-600 transition-colors">
              Sarees
            </Link>
            <Link to="/products?category=western" className="text-gray-600 hover:text-primary-600 transition-colors">
              Western
            </Link>
            <Link to="/products?is_featured=true" className="text-gray-600 hover:text-primary-600 transition-colors">
              New Arrivals
            </Link>
          </div>

          {/* Search */}
          <form onSubmit={handleSearch} className="hidden md:flex items-center">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search dresses..."
                className="pl-4 pr-10 py-2 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 w-52"
              />
              <button type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-600">
                <MagnifyingGlassIcon className="h-4 w-4" />
              </button>
            </div>
          </form>

          {/* Action icons */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <Link to="/wishlist" className="p-2 text-gray-600 hover:text-primary-600 transition-colors relative">
                  <HeartIcon className="h-6 w-6" />
                </Link>
                <Link to="/cart" className="p-2 text-gray-600 hover:text-primary-600 transition-colors relative">
                  <ShoppingBagIcon className="h-6 w-6" />
                  {itemCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                      {itemCount > 99 ? "99+" : itemCount}
                    </span>
                  )}
                </Link>
                <div className="relative group">
                  <button className="p-2 text-gray-600 hover:text-primary-600 transition-colors flex items-center gap-1">
                    <UserIcon className="h-6 w-6" />
                    <span className="text-sm hidden md:block">{user?.full_name.split(" ")[0]}</span>
                  </button>
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <Link to="/profile" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-t-xl">Profile</Link>
                    <Link to="/orders" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">My Orders</Link>
                    <Link to="/support" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">Support</Link>
                    <hr className="border-gray-100" />
                    <button onClick={logout} className="block w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-b-xl">
                      Logout
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Link to="/support" className="p-2 text-gray-600 hover:text-primary-600">
                  <QuestionMarkCircleIcon className="h-6 w-6" />
                </Link>
                <Link to="/login" className="text-gray-600 hover:text-primary-600 text-sm font-medium">
                  Login
                </Link>
                <Link to="/register" className="btn-primary text-sm py-1.5 px-4">
                  Sign Up
                </Link>
              </>
            )}

            {/* Mobile menu */}
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden p-2 text-gray-600"
            >
              {menuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white px-4 py-3 space-y-3">
          <form onSubmit={handleSearch} className="flex">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search dresses..."
              className="flex-1 pl-4 pr-2 py-2 border border-gray-200 rounded-l-full text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
            <button type="submit" className="bg-primary-600 text-white px-4 py-2 rounded-r-full">
              <MagnifyingGlassIcon className="h-4 w-4" />
            </button>
          </form>
          <Link to="/products" onClick={() => setMenuOpen(false)} className="block text-gray-700 py-2">Shop All</Link>
          <Link to="/products?category=kurta" onClick={() => setMenuOpen(false)} className="block text-gray-700 py-2">Kurtas</Link>
          <Link to="/products?category=saree" onClick={() => setMenuOpen(false)} className="block text-gray-700 py-2">Sarees</Link>
          <Link to="/products?category=western" onClick={() => setMenuOpen(false)} className="block text-gray-700 py-2">Western</Link>
          {isAuthenticated && (
            <>
              <Link to="/orders" onClick={() => setMenuOpen(false)} className="block text-gray-700 py-2">My Orders</Link>
              <Link to="/support" onClick={() => setMenuOpen(false)} className="block text-gray-700 py-2">Support</Link>
              <button onClick={() => { logout(); setMenuOpen(false); }} className="block text-red-600 py-2 w-full text-left">Logout</button>
            </>
          )}
        </div>
      )}
    </nav>
  );
}
